import { useEffect, useState } from 'react'
import { api } from '../lib/api'

export type Audiences = Awaited<ReturnType<typeof api.broadcastAudiences>>

/** Who a broadcast can go to, and how many on duty each group reaches NOW.
 *
 *  RE-READ EVERY HALF MINUTE while a picker is on screen, because the number
 *  beside a group is the whole point of it: a member going off duty mid-shift
 *  turns "TOSH 1" into "TOSH 0", and a count read once when the screen opened
 *  would promise a broadcast somebody who has gone home.
 *
 *  A FAILURE LEAVES NULL, and null draws "Everyone on duty" with nothing to
 *  choose. Aiming a broadcast is a refinement; not being able to send one
 *  because this read failed would be the dependency blocking dispatch. */
export function useAudiences(): Audiences | null {
  const [data, setData] = useState<Audiences | null>(null)
  useEffect(() => {
    let alive = true
    const load = () =>
      api.broadcastAudiences().then((a) => { if (alive) setData(a) }).catch(() => {})
    load()
    const t = setInterval(load, 30_000)
    return () => { alive = false; clearInterval(t) }
  }, [])
  return data
}

/** True only when the choice PROVABLY reaches nobody: everyone on duty is
 *  nobody, or every ticked group has nobody on duty in it -- a union of empty
 *  groups is empty. Anything else is the server's to count, since two groups of
 *  one can be the same one person. A group this screen no longer knows (retired
 *  since it was ticked) counts as empty; the server refuses it by name. */
export function reachesNobody(audiences: Audiences | null, value: string[]): boolean {
  if (!audiences) return false
  if (value.length === 0) return audiences.everyone === 0
  const reach = new Map(audiences.groups.map((g) => [g.id, g.reach]))
  return value.every((id) => (reach.get(id) ?? 0) === 0)
}

/** Everyone on duty, or any mix of areas and categories.
 *
 *  THE CUSTOMER'S RULE: "tick all members or only members of certain areas or
 *  only coordinators". Ticked groups are a UNION -- TOSH and Coordinators is
 *  everybody in either -- and ticking any group takes "Everyone" off, because
 *  "everyone AND TOSH" is just everyone and a screen showing both lit would be
 *  a question nobody can answer.
 *
 *  WHO RECEIVES IT, NEVER HOW THE LOCATION IS WRITTEN. The area buttons on the
 *  Take a call box share these names and are a different control on purpose:
 *  "not the group of members receiving the message should define if the
 *  address should be written out". */
export function AudiencePicker({
  audiences, value, onChange, disabled,
}: {
  audiences: Audiences | null
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  if (!audiences) {
    return <div className="audience"><span className="dim">Everyone on duty</span></div>
  }
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  const nobody = reachesNobody(audiences, value)

  return (
    <div className="audience">
      <button
        type="button"
        className={`chip ${value.length === 0 ? 'on' : ''}`}
        disabled={disabled}
        onClick={() => onChange([])}
      >
        Everyone on duty <span className="reach">{audiences.everyone}</span>
      </button>
      {audiences.groups.length > 0 && <span className="or">or only</span>}
      {audiences.groups.map((g) => (
        <button
          type="button"
          key={g.id}
          className={`chip ${value.includes(g.id) ? 'on' : ''} ${g.reach === 0 ? 'empty' : ''}`}
          disabled={disabled}
          // The number is how many are on duty and reachable in the group now,
          // counted the way the broadcast counts them.
          title={`${g.reach} on duty in ${g.name}`}
          onClick={() => toggle(g.id)}
        >
          {g.name} <span className="reach">{g.reach}</span>
        </button>
      ))}
      {nobody && (
        <span className="nobody">
          {value.length === 0
            ? 'Nobody is on duty — this reaches nobody.'
            : `Nobody on duty in ${value.length > 1 ? 'those groups' : 'that group'} — pick another.`}
        </span>
      )}
    </div>
  )
}
